import React, {useEffect, useRef, useState} from 'react'
import './styles.css'
import SilverButton from "../../../../../../../../components-new/buttons/SilverButton";
import BlueButton from "../../../../../../../../components-new/buttons/BlueButton";
import CheckIcon from "../../../../../../../../assets/icons/CheckIcon";
import XIcon from "../../../../../../../../assets/icons/XIcon";
import EditSquareIcon from "../../../../../../../../assets/icons/EditSquareIcon";
import DeleteIcon from "../../../../../../../../assets/icons/DeleteIcon";
import LinkIcon from "../../../../../../../../assets/icons/LinkIcon";
import Tooltip from "../../../../../../../../components-new/general/Tooltip";
import Popup from "../../../../../../../../components-new/ui/Popup";
import Input from "../../../../../../../../components-new/form/Input";
import Translate, {t} from "../../../../../../../../components/Translate";
import {store} from "../../../../../../../../store";
import {normalizeUrl, PLACEHOLDERS, sanitizeHtml, serializeEditor, ZERO_WIDTH_SPACE} from "./formatting";

// what the visitor's name placeholders are called on their buttons
const PLACEHOLDER_LABELS = {'<first_name>': 'firstName', '<last_name>': 'lastName'};

/*
 * The message as it reads when not being edited: the name placeholders become
 * small labelled tokens, so "Hello <first_name>" reads as a sentence with a
 * gap the visitor's name goes into. Only here - while editing they stay text.
 */
const previewHtml = (value) => {
    const user = store.getState().user;
    return sanitizeHtml(value).replace(/&lt;(first_name|last_name)&gt;/g, (match, name) =>
        `<span class="message-editor-token">${t({text: PLACEHOLDER_LABELS[`<${name}>`], user}) || name}</span>`
    );
};

const FORMATTING_TAGS = ['STRONG', 'B', 'EM', 'I', 'U', 'A'];

/*
 * The outermost bold / italic / link the caret is at the very end of, or null.
 * With <b><i>word|</i></b> that is the <b> - the caret has to leave both.
 */
const formattingEndingAt = (root, node, offset) => {
    let outer = null;
    let current = node.nodeType === 1 ? node : node.parentNode;

    while (current && current !== root) {
        if (current.nodeType === 1 && FORMATTING_TAGS.indexOf(current.tagName) !== -1) {
            const rest = document.createRange();
            rest.setStart(node, offset);
            rest.setEnd(current, current.childNodes.length);
            if (rest.toString().split(ZERO_WIDTH_SPACE).join('') !== '') break;
            outer = current;
        }
        current = current.parentNode;
    }
    return outer;
};

const closestLink = (node, root) => {
    while (node && node !== root) {
        if (node.nodeType === 1 && node.tagName === 'A') return node;
        node = node.parentNode;
    }
    return null;
};

/*
 * A bot message in the node editor, edited the way it will look - bold, italic
 * and links are shown as such, never as tags. The message is still stored as
 * HTML; formatting.js converts between the two.
 *
 * The editable area is left to the browser (contentEditable + execCommand):
 * React renders it empty and never touches what is inside, it is filled from
 * the message whenever editing starts or ends and read back on save.
 */
const MessageEditor = ({
                           text = '',
                           defaultEditable = false,
                           onSave,
                           onDelete,
                           style = {},
                           textLimit = -1,
                       }) => {
    const [editable, setEditable] = useState(defaultEditable);
    const [value, setValue] = useState(text);
    const [linkPopupIsOpen, setLinkPopupIsOpen] = useState(false);
    const [linkValue, setLinkValue] = useState('');
    const [editingLink, setEditingLink] = useState(false);
    // `selected`: there is text selected - formatting is only ever applied to text already written
    const [active, setActive] = useState({bold: false, italic: false, link: false, selected: false});

    const ignoreBlurRef = useRef(false);
    const containerRef = useRef(null);
    const editorRef = useRef(null);
    const linkInputRef = useRef(null);
    // where the link goes - the editor loses its selection while the popup has focus
    const savedRangeRef = useRef(null);
    const linkPopupWasOpenRef = useRef(false);

    useEffect(() => {
        if (editorRef.current) editorRef.current.innerHTML = editable ? sanitizeHtml(value) : previewHtml(value);
    }, [value, editable]);

    useEffect(() => {
        const el = editorRef.current;
        if (!editable || !el) return;

        el.focus();
        const range = document.createRange();
        range.selectNodeContents(el);
        range.collapse(false);
        restoreRange(range);

        // <b>/<i> rather than <span style="font-weight: bold">
        document.execCommand('styleWithCSS', false, false);
    }, [editable]);

    useEffect(() => {
        if (!editable) return;

        const updateActive = () => {
            const el = editorRef.current;
            const selection = window.getSelection();
            if (!el || !selection.rangeCount || !el.contains(selection.anchorNode)) return;

            setActive({
                bold: document.queryCommandState('bold'),
                italic: document.queryCommandState('italic'),
                link: !!closestLink(selection.anchorNode, el),
                selected: hasTextSelection(),
            });
        };

        document.addEventListener('selectionchange', updateActive);
        return () => document.removeEventListener('selectionchange', updateActive);
    }, [editable]);

    // typing - the keyboard's own input, which has no handler of ours to go through
    useEffect(() => {
        const el = editorRef.current;
        if (!editable || !el) return undefined;

        const handleBeforeInput = (e) => {
            if (e.inputType !== 'insertText' || e.isComposing || !e.data) return;
            if (!leaveFormattingAtCaret()) return;
            e.preventDefault();
            document.execCommand('insertText', false, e.data);
        };

        el.addEventListener('beforeinput', handleBeforeInput);
        return () => el.removeEventListener('beforeinput', handleBeforeInput);
    }, [editable]);

    useEffect(() => {
        if (linkPopupIsOpen) {
            linkPopupWasOpenRef.current = true;
            if (linkInputRef.current) linkInputRef.current.focus();
        } else if (linkPopupWasOpenRef.current) {
            linkPopupWasOpenRef.current = false;
            // closed without a link (Escape) - back to where the operator was
            if (editable && document.activeElement !== editorRef.current && savedRangeRef.current) {
                restoreRange(savedRangeRef.current);
            }
        }
    }, [linkPopupIsOpen]);

    const restoreRange = (range) => {
        const el = editorRef.current;
        if (!el) return;
        el.focus();
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
    };

    /*
     * Text typed right after bold, italic or a link would carry on in it - the
     * browser continues whatever the character before the caret is. Formatting
     * is only ever put on text that is already there, so new text starts plain:
     * the caret is moved out first.
     *
     * Just after the element is not enough - Chrome puts a caret there straight
     * back inside. It is placed after an invisible character in a plain text
     * node instead, which saving drops (formatting.js).
     */
    const leaveFormattingAtCaret = () => {
        const el = editorRef.current;
        const selection = window.getSelection();
        if (!el || !selection.rangeCount || !selection.isCollapsed || !el.contains(selection.anchorNode)) return false;

        const formatting = formattingEndingAt(el, selection.anchorNode, selection.anchorOffset);
        if (!formatting) return false;

        let text = formatting.nextSibling;
        if (text && text.nodeType === 3) {
            if (text.nodeValue.charAt(0) !== ZERO_WIDTH_SPACE) text.nodeValue = ZERO_WIDTH_SPACE + text.nodeValue;
        } else {
            text = document.createTextNode(ZERO_WIDTH_SPACE);
            formatting.parentNode.insertBefore(text, formatting.nextSibling);
        }

        const range = document.createRange();
        range.setStart(text, 1);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
        return true;
    };

    const hasTextSelection = () => {
        const el = editorRef.current;
        const selection = window.getSelection();
        return !!el && selection.rangeCount > 0 && !selection.isCollapsed
            && el.contains(selection.getRangeAt(0).commonAncestorContainer)
            && selection.toString().trim() !== '';
    };

    const currentRange = () => {
        const el = editorRef.current;
        const selection = window.getSelection();
        if (el && selection.rangeCount && el.contains(selection.getRangeAt(0).commonAncestorContainer)) {
            return selection.getRangeAt(0).cloneRange();
        }
        const range = document.createRange();
        range.selectNodeContents(el);
        range.collapse(false);
        return range;
    };

    /*
     * Only on a selection. Pressed with nothing selected, the browser would
     * instead switch bold on for whatever is typed next - formatting that is
     * not visibly attached to anything, and easy to type on without noticing.
     */
    const applyFormat = (command) => {
        if (!hasTextSelection()) return;
        document.execCommand(command, false, null);
    };

    const insertPlaceholder = (placeholder) => {
        leaveFormattingAtCaret();
        document.execCommand('insertText', false, placeholder);
    };

    const openLinkPopup = () => {
        const range = currentRange();
        const link = closestLink(range.startContainer, editorRef.current);

        // a link goes on selected text - or, with the caret in one, edits that whole link
        if (range.collapsed && !link) return;
        if (link && range.collapsed) range.selectNodeContents(link);

        savedRangeRef.current = range;
        setEditingLink(!!link);
        setLinkValue(link ? link.getAttribute('href') || '' : '');
        setLinkPopupIsOpen(true);
    };

    const applyLink = () => {
        const url = normalizeUrl(linkValue);
        const range = savedRangeRef.current;
        setLinkPopupIsOpen(false);
        if (!range) return;

        restoreRange(range);

        if (!url) {
            // an emptied address takes the link off
            if (editingLink) document.execCommand('unlink', false, null);
            return;
        }
        document.execCommand('createLink', false, url);
    };

    const save = () => {
        const next = editorRef.current ? serializeEditor(editorRef.current) : value;
        setValue(next);
        onSave(next);
        setEditable(false);
        setLinkPopupIsOpen(false);
    };

    const cancel = () => {
        setEditable(false);
        setLinkPopupIsOpen(false);
    };

    // mouse down would move focus out of the editor and lose the selection
    const keepFocus = (e) => e.preventDefault();

    const handleKeyDown = (e) => {
        if ((e.ctrlKey || e.metaKey) && !e.altKey) {
            const key = e.key.toLowerCase();
            if (key === 'b' || key === 'i') {
                e.preventDefault();
                applyFormat(key === 'b' ? 'bold' : 'italic');
                return;
            }
            if (key === 'k') {
                e.preventDefault();
                openLinkPopup();
                return;
            }
            if (key === 'u') {
                e.preventDefault(); // underline has no button, so it is not offered here either
                return;
            }
        }
        if (e.key === 'Enter') {
            e.preventDefault();
            if (!e.shiftKey) {
                save();
                return;
            }
            if (!document.execCommand('insertLineBreak', false, null)) {
                document.execCommand('insertHTML', false, '<br>');
            }
        }
        if (e.key === 'Escape') {
            e.preventDefault();
            cancel();
        }
    };

    // formatting from elsewhere stays elsewhere - only the text is pasted
    const handlePaste = (e) => {
        e.preventDefault();
        const clipboard = e.clipboardData || window.clipboardData;
        leaveFormattingAtCaret();
        document.execCommand('insertText', false, clipboard ? clipboard.getData('text/plain') : '');
    };

    // the limit is on the stored message, tags included - whatever went over it is undone
    const handleInput = () => {
        if (textLimit !== -1 && serializeEditor(editorRef.current).length > textLimit) {
            document.execCommand('undo', false, null);
        }
    };

    const handleLinkKeyDown = (e) => {
        if (e.key === 'Escape') {
            e.stopPropagation();
            setLinkPopupIsOpen(false);
        }
        if (e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
            applyLink();
        }
    };

    // leaving the editor saves - moving between its own text, toolbar and link popup does not
    const handleBlur = (e) => {
        if (!editable || ignoreBlurRef.current) return;
        if (e.relatedTarget && containerRef.current && containerRef.current.contains(e.relatedTarget)) return;
        save();
    };

    // ghost buttons of one size, so the toolbar reads as a single row of tools
    const toolButton = ({key, label, tooltip, shortcut = '', needTranslate = true, onClick, active = false, disabled = false, className = '', children}) => (
        <Tooltip key={key} text={tooltip} shortcut={shortcut} needTranslate={needTranslate} deltaY={-7}>
            <SilverButton
                type="button"
                variant="ghost"
                size="sm"
                iconOnly={!label}
                width={label ? 'auto' : '28px'}
                height="28px"
                className={`message-editor-tool${active && !disabled ? ' is-active' : ''} ${className}`}
                disabled={disabled}
                onMouseDown={keepFocus}
                onClick={onClick}
            >
                {label || children}
            </SilverButton>
        </Tooltip>
    );

    // a click on the text starts editing it - except on a link, which opens
    const handlePreviewClick = (e) => {
        if (editable || (e.target.closest && e.target.closest('a'))) return;
        setEditable(true);
    };

    return (
        <div
            className={'message-editor-container' + (editable ? ' is-editing' : '')}
            ref={containerRef}
            style={style}
            onBlur={handleBlur}
        >
            <div
                ref={editorRef}
                className="message-editor-content"
                contentEditable={editable}
                suppressContentEditableWarning
                onClick={handlePreviewClick}
                onKeyDown={editable ? handleKeyDown : undefined}
                onPaste={editable ? handlePaste : undefined}
                onDrop={editable ? (e) => e.preventDefault() : undefined}
                onInput={editable ? handleInput : undefined}
            />

            {editable ? (
                <div
                    className="message-editor-footer"
                    // a press anywhere on the bar - a gap, a disabled button - leaves the selection alone
                    onMouseDown={(e) => {
                        if (!e.target.closest('input')) e.preventDefault();
                    }}
                >
                    <div className="message-editor-toolbar">
                        {toolButton({
                            key: 'bold',
                            tooltip: 'bold',
                            shortcut: 'Ctrl+B',
                            active: active.bold,
                            disabled: !active.selected,
                            onClick: () => applyFormat('bold'),
                            children: <strong className="message-editor-tool__glyph">B</strong>,
                        })}
                        {toolButton({
                            key: 'italic',
                            tooltip: 'italic',
                            shortcut: 'Ctrl+I',
                            active: active.italic,
                            disabled: !active.selected,
                            onClick: () => applyFormat('italic'),
                            children: <em className="message-editor-tool__glyph">I</em>,
                        })}
                        <Popup
                            isOpen={linkPopupIsOpen}
                            trigger={toolButton({
                                key: 'link',
                                tooltip: 'link',
                                shortcut: 'Ctrl+K',
                                active: active.link,
                                disabled: !active.selected && !active.link,
                                children: <LinkIcon width={15} height={15} stroke="currentColor"/>,
                            })}
                            panelInnerStyle={{padding: '2px', display: 'flex', alignItems: 'center', gap: '4px'}}
                            panelStyle={{padding: '2px', borderRadius: '10px'}}
                            onOpenChange={(open) => open ? openLinkPopup() : setLinkPopupIsOpen(false)}
                            disabled={!active.selected && !active.link}
                            panelAlign={{horizontal: '', vertical: 'bottom'}}
                        >
                            <Input
                                value={linkValue}
                                ref={linkInputRef}
                                placeholder="https://"
                                onChange={e => setLinkValue(e.target.value)}
                                style={{width: '200px', height: '28px'}}
                                onKeyDown={handleLinkKeyDown}
                            />
                            <BlueButton
                                type="button"
                                size="sm"
                                iconOnly
                                width="28px"
                                height="28px"
                                onClick={applyLink}
                                disabled={!normalizeUrl(linkValue) && !editingLink}
                            >
                                <CheckIcon width={15} height={15} stroke="currentColor"/>
                            </BlueButton>
                        </Popup>

                        <span className="message-editor-toolbar__divider"/>

                        {PLACEHOLDERS.map((placeholder) => toolButton({
                            key: placeholder,
                            tooltip: placeholder,
                            needTranslate: false,
                            label: <Translate text={PLACEHOLDER_LABELS[placeholder]}/>,
                            className: 'message-editor-tool--token',
                            onClick: () => insertPlaceholder(placeholder),
                        }))}
                    </div>

                    <div className="message-editor-actions">
                        <Tooltip text="cancel" deltaY={-7}>
                            <SilverButton
                                type="button"
                                variant="ghost"
                                size="sm"
                                iconOnly
                                width="28px"
                                height="28px"
                                onMouseDown={() => {
                                    ignoreBlurRef.current = true;
                                }}
                                onClick={() => {
                                    cancel();
                                    ignoreBlurRef.current = false;
                                }}
                            >
                                <XIcon width={16} height={16} stroke="currentColor"/>
                            </SilverButton>
                        </Tooltip>
                        <Tooltip text="save" deltaY={-7}>
                            <BlueButton
                                type="button"
                                size="sm"
                                iconOnly
                                width="28px"
                                height="28px"
                                onClick={save}
                            >
                                <CheckIcon width={16} height={16} stroke="currentColor"/>
                            </BlueButton>
                        </Tooltip>
                    </div>
                </div>
            ) : (
                <div className="message-editor-actions message-editor-actions--overlay">
                    <Tooltip text="edit" deltaY={-7}>
                        <SilverButton
                            type="button"
                            variant="ghost"
                            size="sm"
                            iconOnly
                            width="28px"
                            height="28px"
                            onClick={() => setEditable(true)}
                        >
                            <EditSquareIcon width={16} height={16} stroke="currentColor"/>
                        </SilverButton>
                    </Tooltip>
                    <Tooltip text="remove" deltaY={-7}>
                        <SilverButton
                            type="button"
                            variant="ghost"
                            size="sm"
                            iconOnly
                            width="28px"
                            height="28px"
                            className="message-editor-tool--danger"
                            onClick={onDelete}
                        >
                            <DeleteIcon width={16} height={16} stroke="currentColor"/>
                        </SilverButton>
                    </Tooltip>
                </div>
            )}
        </div>
    );
};

export default MessageEditor;



// WEBPACK FOOTER //
// ./src/scenes/dashboard/components/graph/containers/NodeEditor/components/MessageEditor/index.jsx